import debounce from "lodash.debounce";
import React, { useEffect, useMemo, useRef, useState } from "react";
import PaymentInputGroup from "./PaymentInputGroup";
import { searchGeoapifyAddress } from "../api";
import {
  GeoapifyFeature,
  GeoapifyProperties,
} from "../types/AddressSearchItem";

const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

type AddressSearchInputProps = {
  placeholder: string;
  name: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  /** Called with the chosen suggestion; the parent fills the address fields. */
  onSelect: (properties: GeoapifyProperties) => void;
  geoapifyKey: string | null;
  required?: boolean;
};

/**
 * Street-address input with a Geoapify autocomplete dropdown.
 * Owns the debounced search, stale-response guarding, keyboard navigation
 * (arrows/Enter/Escape), and click-outside dismissal so the checkout form
 * only has to fill its fields on select.
 */
const AddressSearchInput = ({
  placeholder,
  name,
  value,
  onChange,
  onSelect,
  geoapifyKey,
  required = false,
}: AddressSearchInputProps) => {
  const [results, setResults] = useState<GeoapifyFeature[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  // Monotonic id so a slow earlier response can't overwrite a newer one.
  const requestIdRef = useRef(0);

  // Latest key readable from the once-created debounced closure.
  const geoapifyKeyRef = useRef(geoapifyKey);
  geoapifyKeyRef.current = geoapifyKey;

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!geoapifyKeyRef.current) return;
        if (query.trim().length < MIN_QUERY_LENGTH) {
          setResults([]);
          setOpen(false);
          return;
        }
        const requestId = ++requestIdRef.current;
        const data = await searchGeoapifyAddress(query, geoapifyKeyRef.current);
        if (requestId !== requestIdRef.current) return;
        const features = data?.features ?? [];
        setResults(features);
        setHighlightedIndex(features.length > 0 ? 0 : -1);
        setOpen(features.length > 0);
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange(e);
    debouncedSearch(e.target.value);
  };

  const handleSelect = (properties: GeoapifyProperties) => {
    onSelect(properties);
    setOpen(false);
    setResults([]);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        handleSelect(results[highlightedIndex].properties);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <PaymentInputGroup
        type="text"
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (results.length > 0) setOpen(true);
        }}
        required={required}
      />
      {open && results.length > 0 && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 -mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {results.map((result, index) => (
            <li
              key={result.properties.place_id ?? index}
              role="option"
              aria-selected={index === highlightedIndex}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(result.properties)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`cursor-pointer px-3 py-2 text-sm first:rounded-t-lg last:rounded-b-lg ${
                index === highlightedIndex
                  ? "bg-blue-50 text-gray-900"
                  : "text-gray-700"
              }`}
            >
              <span className="block font-medium">
                {result.properties.address_line1 ?? result.properties.formatted}
              </span>
              {result.properties.address_line2 && (
                <span className="block text-xs text-gray-500">
                  {result.properties.address_line2}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressSearchInput;
